if (!customElements.get('coming-soon-card-signup')) {
  class ComingSoonCardSignup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      if (this.isConnected) return;

      if (this.outsideClickController) {
        this.outsideClickController.abort();
      }
    }

    initialize() {
      const card = this.closest('product-card');
      const template = this.querySelector('template');

      if (!card || !template) return;

      const candidates = Array.from(
        card.querySelectorAll('.group-block-content')
      ).filter((group) => {
        return (
          group.querySelector('[ref="productTitleLink"]') &&
          group.querySelector('product-price') &&
          !group.querySelector('.card-gallery, slideshow-component') &&
          !group.closest('a')
        );
      });

      const details = candidates.find((group) => {
        return !candidates.some(
          (other) => other !== group && group.contains(other)
        );
      });

      if (!details) {
        console.warn(
          'Coming Soon signup: no separate title/price group was found.'
        );
        return;
      }

      const original = document.createElement('div');
      original.className = 'cs-signup-original';

      while (details.firstChild) {
        original.append(details.firstChild);
      }

      const opener = document.createElement('button');
      opener.type = 'button';
      opener.className = 'cs-signup-open';
      opener.textContent = this.dataset.submitLabel;
      opener.setAttribute('aria-expanded', 'false');

      original.append(opener);
      details.append(original);
      details.append(this);

      this.append(template.content.cloneNode(true));
      template.remove();

      details.classList.add('cs-signup-details');
      this.setAttribute('data-ready', '');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;

      const form = this.querySelector('form');
      const email = this.querySelector('[name="email"]');
      const size = this.querySelector('[name="size"]');
      const submit = this.querySelector('[type="submit"]');
      const close = this.querySelector('.cs-signup__close');
      const status = this.querySelector('.cs-signup__status');

      const persistentTitle = original.querySelector(
        '[ref="productTitleLink"]'
      );

      if (persistentTitle) {
        persistentTitle.classList.add('cs-signup-persistent-title');
        details.prepend(persistentTitle);
      }

      email.setAttribute('aria-describedby', status.id);
      size.setAttribute('aria-describedby', status.id);

      let editing = false;
      let sending = false;
      let completed = false;
      let dismissed = false;

      const announce = (message) => {
        status.textContent = '';
        requestAnimationFrame(() => {
          status.textContent = message;
        });
      };

      const openPanel = () => {
        if (dismissed) return;

        details.classList.add('is-signup-open');
        original.inert = true;
        original.setAttribute('aria-hidden', 'true');
        this.inert = false;
        this.setAttribute('aria-hidden', 'false');
        opener.setAttribute('aria-expanded', 'true');
      };

      const closePanel = (returnFocus) => {
        this.querySelectorAll(
          '.waitlist-size-picker__panel'
        ).forEach((panel) => {
          if (panel.matches(':popover-open')) {
            panel.hidePopover();
          }
        });

        details.classList.remove('is-signup-open');
        original.inert = false;
        original.removeAttribute('aria-hidden');
        this.inert = true;
        this.setAttribute('aria-hidden', 'true');
        opener.setAttribute('aria-expanded', 'false');

        if (returnFocus) opener.focus();
      };


      opener.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dismissed = false;
        openPanel();

        if (
          event.detail === 0 ||
          window.matchMedia('(hover: hover) and (pointer: fine)').matches
        ) {
          email.focus({ preventScroll: true });
        }
      });

      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        dismissed = false;

        if (!original.contains(document.activeElement)) {
          openPanel();
        }
      });

      card.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;

        if (
          event.relatedTarget instanceof Node &&
          card.contains(event.relatedTarget)
        ) {
          return;
        }

        closePanel(false);
      });

      this.outsideClickController = new AbortController();

      document.addEventListener('pointerdown', (event) => {
        if (!card.contains(event.target)) {
          closePanel(false);
        }
      }, {
        capture: true,
        signal: this.outsideClickController.signal
      });

      this.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });

      email.addEventListener('mousedown', (event) => {
        if (
          window.matchMedia(
            '(max-width: 749px), (hover: none)'
          ).matches
        ) {
          event.stopPropagation();
        }
      });

      email.addEventListener('click', (event) => {
        if (
          !window.matchMedia(
            '(max-width: 749px), (hover: none)'
          ).matches
        ) {
          return;
        }

        event.stopPropagation();

        if (
          !email.disabled &&
          !email.readOnly &&
          !email.closest('[inert]')
        ) {
          email.focus({ preventScroll: true });
        }
      });

      form.addEventListener('input', () => {
        editing = true;
      });

      form.addEventListener('change', () => {
        editing = true;
      });

      close.addEventListener('click', () => {
        dismissed = true;
        closePanel(true);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        event.stopPropagation();
        dismissed = true;
        closePanel(true);
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (sending || completed || !form.reportValidity()) return;

        sending = true;
        editing = true;
        submit.disabled = true;
        submit.textContent = this.dataset.sendingLabel;
        form.setAttribute('aria-busy', 'true');
        announce('');

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        try {
          const response = await fetch(this.dataset.workerUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: email.value.trim(),
              product_handle: this.dataset.productHandle,
              variant_title: size.value
            }),
            signal: controller.signal
          });

          if (!response.ok) {
            throw new Error(
              'Waitlist request failed: HTTP ' + response.status
            );
          }

          if (
            (response.headers.get('content-type') || '').includes(
              'application/json'
            )
          ) {
            const result = await response.json();

            if (
              result &&
              (result.success === false || result.error)
            ) {
              throw new Error('Waitlist request was rejected');
            }
          }

          completed = true;
          status.dataset.state = 'success';
          announce(this.dataset.successMessage);
          submit.textContent = 'Registered ✓';
          email.readOnly = true;
          size.disabled = true;
        } catch (error) {
          console.error(
            'Coming Soon waitlist signup failed:',
            error
          );
          announce(this.dataset.errorMessage);
          submit.disabled = false;
          submit.textContent = this.dataset.submitLabel;
        } finally {
          clearTimeout(timeout);
          sending = false;
          form.removeAttribute('aria-busy');
        }
      });
    }
  }

  customElements.define(
    'coming-soon-card-signup',
    ComingSoonCardSignup
  );
}
